import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

export type PermissionGroupTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'permission_group_created'
  | 'permission_group_name_updated'
  | 'permission_group_permissions_updated'
  | 'permission_group_deleted'
>;

type PermissionGroupParams = Pretty<
  Exclude<
    paths['/api/permission-group']['get']['parameters']['query'],
    undefined
  > & {
    include_summary?: boolean;
  }
>;

/**
 * The backend API returns a paginated response only when the page_size parameter is present.
 * The generic 'paginated' argument forces the type of page_size to be required. This is needed when
 * the params argument is not inlined in the function call for the api and query utilities to make the
 * return type inference in {@link GetPermissionGroupsResponse} will give you the correct result
 */
export type GetPermissionGroupsParams<
  T extends 'paginated' | undefined = undefined,
> = T extends 'paginated'
  ? Pretty<Omit<PermissionGroupParams, 'page_size'> & { page_size: number }>
  : PermissionGroupParams;

// PermissionGroup & PermissionGroupRequest are almost entirely incomplete
type PermissionGroupRequest = Record<string, any> &
  paths['/api/permission-group']['post']['requestBody']['content']['application/json'];

export interface PermissionGroup
  extends Mutable<
    paths['/api/permission-group']['post']['responses']['201']['content']['application/json']
  > {
  [k: string]: any;
}

type ListItem =
  paths['/api/permission-group']['get']['responses']['200']['content']['application/json']['results'][number];

export interface PermissionGroupListItem extends ListItem {
  summary: {
    group: string;
    nb_remove: number;
    nb_edit: number;
    nb_view: number;
    nb_none: number;
  };
}

export type GetPermissionGroupsResponse<
  TParams extends GetPermissionGroupsParams | undefined = undefined,
> = TParams extends { page_size: number }
  ? PaginatedResponse<PermissionGroupListItem>
  : PermissionGroupListItem[];

export type PermissionGroupHistory = Omit<
  paths['/api/permission-group/{permission_group_id}/history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: PermissionGroupTimelineEntry[];
};

export type PermissionGroupMetadata =
  paths['/api/permissions/meta-data']['get']['responses']['200']['content']['application/json'];

export type PermissionGroupHistoryParams = Required<
  paths['/api/permission-group/{permission_group_id}/history']['get']['parameters']
>['query'];

export const createPermissionGroup = async (
  instance: AxiosInstance,
  data: PermissionGroupRequest,
  options?: RequestOptions
): Promise<PermissionGroup> => {
  const res = await instance.post<PermissionGroup>(
    '/permission-group',
    data,
    options
  );

  return res.data;
};

export const getPermissionGroup = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<PermissionGroup> => {
  const res = await instance.get<PermissionGroup>(
    `/permission-group/${id}`,
    options
  );

  return res.data;
};

export const getPermissionGroups = async <
  TParams extends GetPermissionGroupsParams | undefined = undefined,
>(
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<GetPermissionGroupsParams>
): Promise<GetPermissionGroupsResponse<TParams>> => {
  const res = await instance.get<GetPermissionGroupsResponse<TParams>>(
    '/permission-group',
    options
  );

  return res.data;
};

export const getPermissionGroupHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<PermissionGroupHistoryParams>
): Promise<PermissionGroupHistory> => {
  const res = await instance.get<PermissionGroupHistory>(
    `/permission-group/${id}/history`,
    options
  );

  return res.data;
};

export const getPermissionGroupMetadata = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<PermissionGroupMetadata> => {
  const res = await instance.get<PermissionGroupMetadata>(
    '/permissions/meta-data',
    options
  );

  return res.data;
};

export class PermissionGroupService {
  public createPermissionGroup;
  public getPermissionGroup;
  public getPermissionGroups;
  public getPermissionGroupHistory;
  public getPermissionGroupMetadata;

  constructor(private instance: AxiosInstance) {
    this.createPermissionGroup = curry1(createPermissionGroup)(this.instance);
    this.getPermissionGroup = curry1(getPermissionGroup)(this.instance);
    this.getPermissionGroups = curry1(getPermissionGroups)(this.instance);
    this.getPermissionGroupHistory = curry1(getPermissionGroupHistory)(
      this.instance
    );
    this.getPermissionGroupMetadata = curry1(getPermissionGroupMetadata)(
      this.instance
    );
  }
}
